import { useEffect, useRef, useState } from 'react'
import type { PublicAd } from '@gymboard/types'
import { apiAsset, screenApi } from '../lib/api'
import { isFeatureDisabled } from './useScreenFeatures'

const REFRESH_MS = 120_000

/**
 * The gym's live TV ads. A failed fetch keeps the last list, and every image
 * is preloaded so a rotation never lands on a blank card. Cleared when the
 * gym switches ads off (`enabled` false, or the API says FEATURE_DISABLED).
 */
export function useScreenAds(enabled = true): PublicAd[] {
  const [ads, setAds] = useState<PublicAd[]>([])

  useEffect(() => {
    if (!enabled) {
      setAds([])
      return
    }
    let cancelled = false
    const load = async () => {
      try {
        const res = await screenApi.getAds()
        if (cancelled) return
        const list = Array.isArray(res.data) ? res.data : []
        for (const ad of list) if (ad.imageUrl) new Image().src = apiAsset(ad.imageUrl)
        setAds(list)
      } catch (err) {
        // keep the last good list — unless ads were switched off
        if (!cancelled && isFeatureDisabled(err)) setAds([])
      }
    }
    load()
    const interval = setInterval(load, REFRESH_MS)
    return () => {
      cancelled = true
      clearInterval(interval)
    }
  }, [enabled])

  return ads
}

/** Each ad holds the screen this long. One TV "view" = one turn. */
export const AD_TURN_MS = 12_000

/**
 * Which ad is showing, and the impression for each turn. Lives with the idle
 * screen (not the card) so a layout change, like the gym board arriving and
 * the ad moving columns, neither restarts the rotation nor counts twice.
 */
export function useAdRotation(ads: PublicAd[]) {
  const [turn, setTurn] = useState(0)
  const reported = useRef<string | null>(null)
  const ad = ads.length ? ads[turn % ads.length] : null

  useEffect(() => {
    if (!ads.length) return
    const t = setInterval(() => setTurn((n) => n + 1), AD_TURN_MS)
    return () => clearInterval(t)
  }, [ads.length])

  useEffect(() => {
    if (!ad || document.hidden) return
    // A turn of the same ad (only one live) is another 12 s on screen.
    const key = `${turn}:${ad.id}`
    if (reported.current === key) return
    reported.current = key
    screenApi.reportAdImpression(ad.id).catch(() => {})
  }, [turn, ad?.id])

  return { ad, index: ads.length ? turn % ads.length : 0 }
}
